import { type AsyncComponentLoader, type Component, defineAsyncComponent } from "vue"
import { logger } from "./logger"

// Recovery for stale lazy-loaded chunks after a deploy.
//
// The app code-splits views (App.vue's `defineAsyncComponent(() => import(...))`)
// into hashed chunks. When a new version ships, the old hashed filenames stop
// existing. A page that's still running the previous build keeps references to
// those old names, so navigating to a not-yet-loaded view fetches a `.js` that
// is gone — and the SPA host (Firebase Hosting) answers the 404 with
// `index.html`. The browser then refuses to run HTML as a module and throws:
//
//   Safari:  'text/html' is not a valid JavaScript MIME type.
//   Chrome:  Failed to fetch dynamically imported module: <url>
//   Firefox: error loading dynamically imported module
//
// The cure is to reload once: the fresh `index.html` references the new chunk
// hashes and the navigation then succeeds. A short cooldown guards against
// reload loops when the failure is something a reload can't fix (genuinely
// offline, a broken deploy), so we degrade to surfacing the error instead.

const RELOAD_FLAG = "chunkReload:lastAttempt"
const RELOAD_COOLDOWN_MS = 10_000

const STALE_CHUNK_PATTERNS = [
  "is not a valid javascript mime type", // Safari
  "failed to fetch dynamically imported module", // Chromium
  "error loading dynamically imported module", // Firefox
  "importing a module script failed", // Safari (alt wording)
  "expected a javascript module script", // strict MIME check
  "dynamically imported module", // catch-all for the above family
]

/** True when an error looks like a missing/replaced lazy chunk rather than a
 * genuine runtime error in the module's own code. */
export function isStaleChunkError(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error ?? "")).toLowerCase()
  return STALE_CHUNK_PATTERNS.some((pattern) => message.includes(pattern))
}

function reloadedRecently(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0)
    return Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

/** Reload once to pick up the new build. Returns false (and does nothing) if we
 * already reloaded within the cooldown, so callers can fall back to surfacing
 * the error instead of spinning in a reload loop. */
export function reloadForStaleChunk(): boolean {
  if (reloadedRecently()) return false
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode quota, etc.) — still reload, but
    // without the loop guard. The browser cache makes a single extra reload
    // cheap, and a real deploy will load fresh chunks on the first try.
  }
  logger.warn("Reloading to recover from a stale app chunk (likely a new deploy)")
  window.location.reload()
  return true
}

/** Lazy-load a view like `defineAsyncComponent`, but recover from a stale chunk
 * by reloading instead of leaving the user on a blank/broken screen. The global
 * guard below normally fires first; this is a fallback for when the loader
 * rejects without a `vite:preloadError` (e.g. the chunk evaluates but a nested
 * import fails). */
export function defineLazyView(loader: AsyncComponentLoader): Component {
  return defineAsyncComponent({
    loader,
    onError(error, _retry, fail) {
      if (isStaleChunkError(error) && reloadForStaleChunk()) return
      fail()
    },
  })
}

/** Install the global recovery hook. Vite fires `vite:preloadError` whenever a
 * lazily imported chunk fails to load; that's the broadest signal and covers
 * every `import()` in the app, including ones nested inside components. */
export function installChunkReloadGuard(): void {
  if (typeof window === "undefined") return
  window.addEventListener("vite:preloadError", (event) => {
    // Prevent Vite from re-throwing; we're handling it by reloading.
    event.preventDefault()
    reloadForStaleChunk()
  })
}
